import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Layout, message, Input, Form, Grid } from "antd";
import ResponsiveModal from '../Shared/ResponsiveModal/ResponsiveModal';
import { useTranslation } from 'react-i18next';
import TransactionTable from "./TransactionTable/TransactionTable";
import TransactionDetailsModal from "./TransactionModal/TransactionDetailsModal";
import AddTransactionModal from "./TransactionModal/AddTransactionModal";
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { reconcileTransaction, updateTransaction } from './TransactionsServices';
import useVisualHighlight from "../../Hooks/useVisualHighlight";
import { useTheme } from "../Shared/GlobalContext/ThemeContext";
import { useGlobal } from "../Shared/GlobalContext/GlobalContext";
import useSocket from "../../Hooks/useSocket";
import useSocketSubscription from "../../Hooks/useSocketSubscription";
import useSocketEvent from "../../Hooks/useSocketEvent";
import { Resource, Action } from "../../Constants/socket";
import { resolveBackendMessage } from '../../Utils/apiMessages';

const { TextArea } = Input;

const Transactions = () => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint() || {};
    const isMobile = !!screens?.xs;
    const [visibleModal, setVisibleModal] = useState(false);
    const [actionToConfirm, setActionToConfirm] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
    const [showTransactionDetailsModal, setShowTransactionDetailsModal] = useState(false);
    const [selectedTransactionDetails, setSelectedTransactionDetails] = useState(null);
    const [refreshTable, setRefreshTable] = useState(false);
    const { handleError } = useErrorHandler();
    const [transactions, setTransactions] = useState([]);
    const [disablingReason, setDisablingReason] = useState('');
    const [form] = Form.useForm();
    const [softRefreshKey, setSoftRefreshKey] = useState(0);

    // Visual highlights for real-time socket events
    const { highlightRow, getHighlightClass } = useVisualHighlight();

    // Socket connection
    const { socket } = useSocket();
    const { user: { id: currentUserId, is_admin } = {} } = useGlobal();

    // Subscribe to user-scoped channel instead of global transaction resource
    useSocketSubscription(Resource.USER, currentUserId ? [currentUserId] : null, true);

    // Memoize transaction IDs for socket subscription
    const transactionIds = useMemo(() => {
        return transactions.map(transaction => transaction.id).filter(Boolean);
    }, [transactions]);

    // Subscribe to transaction resource with specific IDs
    useSocketSubscription(Resource.TRANSACTION, transactionIds.length > 0 ? transactionIds : null);

    const reconcileTransactionCall = useCallback(async (id) => {
        try {
            const payload = await reconcileTransaction(id, { is_reconciled: true });
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            setRefreshTable(prev => !prev);
        } catch (error) {
            handleError(error);
        }
    }, [handleError, setRefreshTable, t]);

    const handleAction = (action, record) => {
        if (action === 'view') {
            setSelectedTransactionDetails(record);
            setShowTransactionDetailsModal(true);
            return;
        }
        if (action === 'reconcile' && is_admin && record.transaction_type === 'payment') {
            reconcileTransactionCall(record.id);
            return;
        }
        showConfirmationModal(action, record);
    };

    const showConfirmationModal = (action, record) => {
        setActionToConfirm(action);
        setSelectedRecord(record);
        setDisablingReason('');
        form.resetFields();
        setVisibleModal(true);
    };

    const handleActionConfirmation = useCallback(async () => {
        // For disable action, validate the form first
        if (actionToConfirm === 'disable') {
            try {
                await form.validateFields();
            } catch (error) {
                return; // Don't proceed if validation fails
            }
        }

        setVisibleModal(false);
        try {
            const { id } = selectedRecord;
            const is_disabled = actionToConfirm === 'disable';

            // Include disabling reason when disabling a transaction
            const payload = is_disabled
                ? { is_disabled, disabling_reason: disablingReason }
                : { is_disabled };

            const result = await updateTransaction(id, payload);
            const text = resolveBackendMessage(t, result);
            if (text) message.success(text);
            setRefreshTable(prev => !prev);
        } catch (error) {
            handleError(error);
        } finally {
            setActionToConfirm(null);
            setSelectedRecord(null);
            setDisablingReason('');
        }
    }, [actionToConfirm, selectedRecord, handleError, setActionToConfirm, setSelectedRecord, setVisibleModal, t, disablingReason, form]);

    const handleTransactionAdded = () => {
        setRefreshTable(prev => !prev);
    };

    // Socket event handlers
    const handleTransactionCreated = useCallback((data) => {
        // Avoid skeleton flash: do a silent/soft refresh in the table and briefly highlight the new row
        if (data?.id) highlightRow(data.id);
        setSoftRefreshKey(prev => prev + 1);

        // Subscribe to new transaction from server_datetime to cover the readiness gap
        if (data?.id && data?.server_datetime && socket) {
            socket.subscribeToResource(Resource.TRANSACTION, [data.id], data.server_datetime);
        }
    }, [highlightRow, socket]);

    const handleTransactionUpdated = useCallback((data) => {
        // Update the specific transaction in state without reloading
        setTransactions(prevTransactions =>
            prevTransactions.map(transaction =>
                transaction.id === data.id ? { ...transaction, ...data } : transaction
            )
        );
        if (data?.id) highlightRow(data.id, 'UPDATE');
    }, [highlightRow]);

    // Register socket event listeners
    useSocketEvent(Resource.TRANSACTION, Action.CREATE, handleTransactionCreated);
    useSocketEvent(Resource.TRANSACTION, Action.UPDATE, handleTransactionUpdated);

    // Callback to receive transactions data from the table
    const handleTransactionsLoaded = useCallback((data) => {
        setTransactions(data);
    }, []);

    const renderModalContent = () => {
        if (actionToConfirm === 'disable') {
            return (
                <Form form={form} layout="vertical">
                    <p>{t('are_you_sure_you_want_to_action_the_transaction', { action: actionToConfirm })}</p>
                    <Form.Item
                        name="disablingReason"
                        label={t('disabling_reason')}
                        rules={[
                            {
                                required: true,
                                message: t('please_provide_a_reason_for_disabling_this_transaction')
                            }
                        ]}
                    >
                        <TextArea
                            value={disablingReason}
                            onChange={(e) => setDisablingReason(e.target.value)}
                            placeholder={t('disabling_reason')}
                            rows={4}
                            dir="auto"
                            style={{ unicodeBidi: 'plaintext' }}
                        />
                    </Form.Item>
                </Form>
            );
        }

        return <p>{t('are_you_sure_you_want_to_action_the_transaction', { action: actionToConfirm })}</p>;
    };

    return (
        <Layout theme={isDarkMode ? 'dark' : 'light'}>
            <TransactionTable
                onAction={handleAction}
                onShowAddTransactionModal={() => setShowAddTransactionModal(true)}
                refreshTable={refreshTable}
                onTransactionsLoaded={handleTransactionsLoaded}
                externalTransactions={transactions}
                softRefreshKey={softRefreshKey}
                getHighlightClass={getHighlightClass}
            />
            <ResponsiveModal
                title={t('confirm_action', { action: actionToConfirm })}
                open={visibleModal}
                onOk={handleActionConfirmation}
                onCancel={() => setVisibleModal(false)}
                okText={t('confirm')}
                cancelText={t('cancel')}
                desktopWidth={420}
            >
                {renderModalContent()}
            </ResponsiveModal>
            <TransactionDetailsModal
                visible={showTransactionDetailsModal}
                onCancel={() => setShowTransactionDetailsModal(false)}
                transactionDetails={selectedTransactionDetails}
            />
            {showAddTransactionModal && <AddTransactionModal
                onCancel={() => setShowAddTransactionModal(false)}
                onTransactionAdded={handleTransactionAdded}
            />}
        </Layout>
    );
};

export default Transactions;
